/**
 * Admin Job Management Page
 * Manage countries, roles, and questionnaires
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/Layout/MainLayout';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdPublic, 
  MdWork, 
  MdQuiz,
  MdSave,
  MdCancel
} from 'react-icons/md';
import { countryService, jobRoleService, questionnaireService } from '../services/jobManagementService';

const AdminJobManagementPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('countries');
  const [countries, setCountries] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [editingItem, setEditingItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('country');
  const [formData, setFormData] = useState({ label: '', value: '', agreementAmount: '', agreementFile: null });
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [editingRole, setEditingRole] = useState(null);

  useEffect(() => {
    // Load all data
    loadCountries();
    loadJobRoles();
    loadQuestionnaires();
  }, []);

  const loadCountries = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Loading countries...');
      
      const response = await countryService.getAll(false); // Only load active countries
      console.log('📊 Countries response:', response);
      
      if (response && response.data) {
        setCountries(response.data);
        console.log('✅ Countries loaded:', response.data.length);
        toast.success(`Loaded ${response.data.length} countries`);
      } else {
        console.log('⚠️ No data in response');
        setCountries([]);
      }
    } catch (error) {
      console.error('❌ Load countries error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      setError(`Failed to load countries: ${error.response?.data?.error || error.message}`);
      toast.error(`Failed to load countries: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadJobRoles = async () => {
    try {
      console.log('🔍 Loading job roles...');
      const response = await jobRoleService.getAll(false); // Only load active roles
      console.log('📊 Job roles response:', response);
      setJobRoles(response.data || []);
      console.log('✅ Job roles loaded:', response.data?.length || 0);
    } catch (error) {
      console.error('❌ Load job roles error:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to load job roles');
    }
  };

  const loadQuestionnaires = async () => {
    try {
      console.log('🔍 Loading questionnaires...');
      const response = await questionnaireService.getAll(false); // Only load active questionnaires
      console.log('📊 Questionnaires response:', response);
      console.log('📊 Questionnaires data:', response.data);
      
      if (response && response.data) {
        setQuestionnaires(response.data);
        console.log('✅ Questionnaires loaded:', response.data.length);
        console.log('📋 Questionnaire details:', response.data.map(q => ({
          id: q._id,
          title: q.title,
          jobRoleId: q.jobRoleId,
          questionsCount: q.questions?.length || 0
        })));
      } else {
        console.log('⚠️ No questionnaire data in response');
        setQuestionnaires([]);
      }
    } catch (error) {
      console.error('❌ Load questionnaires error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error('Failed to load questionnaires');
    }
  };

  // Country Management
  const handleAddCountry = async (countryData) => {
    try {
      setLoading(true);
      
      if (editingItem) {
        // Update existing country
        const response = await countryService.update(editingItem._id, {
          name: countryData.label,
          code: countryData.value ? countryData.value.toUpperCase() : undefined,
          agreementAmount: countryData.agreementAmount,
          agreementFile: countryData.agreementFile
        });
        
        if (response.success) {
          toast.success('Country updated successfully');
          await loadCountries();
          setShowAddModal(false);
          setEditingItem(null);
        }
      } else {
        // Create new country
        const response = await countryService.create({
          name: countryData.label,
          code: countryData.value ? countryData.value.toUpperCase() : undefined,
          agreementAmount: countryData.agreementAmount,
          agreementFile: countryData.agreementFile
        });
        
        if (response.success) {
          toast.success('Country created successfully');
          await loadCountries();
          setShowAddModal(false);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${editingItem ? 'update' : 'create'} country`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCountry = async (countryId) => {
    if (window.confirm('Are you sure you want to delete this country? This will also affect associated job roles.')) {
      try {
        setLoading(true);
        console.log('🗑️ Deleting country:', countryId);
        const response = await countryService.delete(countryId);
        console.log('📊 Delete response:', response);
        
        if (response.success) {
          toast.success('Country deleted successfully');
          // Clear countries first to force a refresh
          setCountries([]);
          await loadCountries();
          await loadJobRoles(); // Refresh job roles as they might be affected
        } else {
          console.log('⚠️ Delete failed:', response);
          toast.error(response.error || 'Failed to delete country');
        }
      } catch (error) {
        console.error('❌ Delete country error:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        toast.error(error.response?.data?.error || 'Failed to delete country');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditCountry = (country) => {
    setEditingItem(country);
    setFormData({
      label: country.name,
      value: country.code || '',
      agreementAmount: country.agreementAmount || '',
      agreementFile: null
    });
    setModalType('country');
    setShowAddModal(true);
  };

  // Role Management
  const handleAddRole = async (roleData) => {
    if (!selectedCountry) {
      toast.error('Please select a country first');
      return;
    }

    try {
      setLoading(true);

      if (editingRole) {
        const response = await jobRoleService.update(editingRole._id, {
          title: roleData.label,
          description: roleData.description || '',
          agreementAmount: roleData.agreementAmount,
          agreementFile: roleData.agreementFile || null
        });
        if (response.success) {
          toast.success('Job role updated successfully');
          await loadJobRoles();
          setShowAddModal(false);
          setEditingRole(null);
        }
      } else {
        const response = await jobRoleService.create({
          title: roleData.label,
          description: roleData.description || '',
          countryId: selectedCountry,
          agreementAmount: roleData.agreementAmount,
          agreementFile: roleData.agreementFile || null
        });
        if (response.success) {
          toast.success('Job role created successfully');
          await loadJobRoles();
          setShowAddModal(false);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.error || `Failed to ${editingRole ? 'update' : 'create'} job role`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setShowAddModal(true);
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Are you sure you want to delete this job role?')) {
      try {
        setLoading(true);
        console.log('🗑️ Deleting role:', roleId);
        const response = await jobRoleService.delete(roleId);
        console.log('📊 Delete response:', response);
        
        if (response.success) {
          toast.success('Job role deleted successfully');
          // Clear job roles first to force a refresh
          setJobRoles([]);
          await loadJobRoles();
        } else {
          console.log('⚠️ Delete failed:', response);
          toast.error(response.error || 'Failed to delete job role');
        }
      } catch (error) {
        console.error('❌ Delete role error:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        toast.error(error.response?.data?.error || 'Failed to delete job role');
      } finally {
        setLoading(false);
      }
    }
  };

  // Questionnaire Management
  const handleAddQuestion = async (questionData) => {
    if (!selectedRole) {
      toast.error('Please select a role first');
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('🔍 Adding question for role:', selectedRole);
      console.log('📝 Question data:', questionData);
      
      // Transform question data to match backend expectations
      const transformedQuestion = {
        questionText: questionData.question,
        fieldType: questionData.type,
        required: questionData.required === 'true' || questionData.required === true,
        options: questionData.options || undefined,
        placeholder: questionData.placeholder || undefined
      };
      
      console.log('🔄 Transformed question:', transformedQuestion);
      
      // Find existing questionnaire or create new one
      let questionnaire = questionnaires.find(q => {
        const roleId = typeof q.jobRoleId === 'string' ? q.jobRoleId : q.jobRoleId?._id;
        return roleId === selectedRole;
      });
      
      console.log('🔍 Found existing questionnaire:', questionnaire);
      
      if (!questionnaire) {
        // Create new questionnaire with auto-generated title
        const jobRole = jobRoles.find(r => r._id === selectedRole);
        const questionnaireTitle = `${jobRole?.title} Application Questionnaire`;
        
        const response = await questionnaireService.create({
          jobRoleId: selectedRole,
          title: questionnaireTitle,
          description: `Application questionnaire for ${jobRole?.title} position`,
          questions: [transformedQuestion]
        });
        
        if (response.success) {
          toast.success('Questionnaire created with question');
          await loadQuestionnaires();
        }
      } else {
        // Update existing questionnaire
        const updatedQuestions = [...questionnaire.questions, transformedQuestion];
        const response = await questionnaireService.update(questionnaire._id, {
          questions: updatedQuestions
        });
        
        if (response.success) {
          toast.success('Question added successfully');
          await loadQuestionnaires();
        }
      }
      
      setShowAddModal(false);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add question');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionnaireId, questionIndex) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      try {
        setLoading(true);
        const questionnaire = questionnaires.find(q => q._id === questionnaireId);
        const updatedQuestions = questionnaire.questions.filter((_, index) => index !== questionIndex);
        
        const response = await questionnaireService.update(questionnaireId, {
          questions: updatedQuestions
        });
        
        if (response.success) {
          toast.success('Question deleted successfully');
          await loadQuestionnaires();
        }
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to delete question');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditQuestion = async (questionnaireId, questionIndex, updatedData) => {
    try {
      setLoading(true);
      
      // Transform question data to match backend expectations
      const transformedData = {
        questionText: updatedData.question || updatedData.questionText,
        fieldType: updatedData.type || updatedData.fieldType,
        required: updatedData.required === 'true' || updatedData.required === true,
        options: updatedData.options || undefined,
        placeholder: updatedData.placeholder || undefined
      };
      
      const questionnaire = questionnaires.find(q => q._id === questionnaireId);
      const updatedQuestions = questionnaire.questions.map((q, index) => 
        index === questionIndex ? {...q, ...transformedData} : q
      );
      
      const response = await questionnaireService.update(questionnaireId, {
        questions: updatedQuestions
      });
      
      if (response.success) {
        toast.success('Question updated successfully');
        await loadQuestionnaires();
      }
      
      setEditingItem(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update question');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = () => {
    toast.success('All changes are automatically saved to the database!');
  };

  const renderCountriesTab = () => (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center pb-4 border-b-2 border-gray-100">
        <h2 className="flex items-center gap-3 text-xl text-[var(--color-primary)] m-0"><MdPublic size={24} /> Countries</h2>
        <button type="button" className="flex items-center gap-2 px-5 py-2.5 bg-[var(--color-accent-green)] text-white border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out hover:bg-[var(--color-accent-green-dark)]" onClick={() => {
          setEditingItem(null);
          setFormData({ label: '', value: '', agreementAmount: '', agreementFile: null });
          setModalType('country');
          setShowAddModal(true);
        }}>
          <MdAdd size={20} /> Add Country
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading countries...</p>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4">
          {countries.map(country => (
            <div key={country._id} className="bg-gray-50 border border-gray-100 rounded-md p-5 flex justify-between items-start transition-all duration-200 ease-in-out hover:border-[var(--color-accent-green)] hover:shadow-sm">
              <div>
                <h3 className="text-base text-[var(--color-primary)] m-0 mb-1">{country.name}</h3>
                <p className="text-xs text-gray-400 m-0 mb-2 font-mono">{country.code}</p>
                <p className="text-[13px] text-gray-600 m-0">
                  {jobRoles.filter(role => role.countryId?._id === country._id).length} roles
                </p>
                <p className="text-[11px] text-gray-500 m-0 mt-1">
                  Status: {country.isActive ? 'Active' : 'Inactive'}
                </p>
                <p className="text-[11px] text-gray-500 m-0 mt-1">
                  Agreement: {country.agreementFileUrl ? 'Configured' : 'Missing'}
                </p>
                <p className="text-[11px] text-gray-500 m-0 mt-1">
                  Amount: {country.agreementAmount !== undefined && country.agreementAmount !== null ? country.agreementAmount : 'Not set'}
                </p>
                {country.agreementFileUrl && (
                  <a
                    href={country.agreementFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[12px] text-[#007bff] mt-1 inline-block"
                  >
                    View Agreement
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-blue-600 text-white hover:bg-blue-800"
                  onClick={() => handleEditCountry(country)}
                  title="Edit"
                >
                  <MdEdit size={18} />
                </button>
                <button 
                  className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-red-600 text-white hover:bg-red-800"
                  onClick={() => handleDeleteCountry(country._id)}
                  title="Delete"
                >
                  <MdDelete size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && modalType === 'country' && (
        <AddCountryModal 
          onClose={() => {
            setShowAddModal(false);
            setEditingItem(null);
            setFormData({ label: '', value: '', agreementAmount: '', agreementFile: null });
          }}
          onAdd={handleAddCountry}
          editingItem={editingItem}
          formData={formData}
          setFormData={setFormData}
        />
      )}
    </div>
  );

  const renderRolesTab = () => (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center pb-4 border-b-2 border-gray-100 flex-col md:flex-row gap-3">
        <h2 className="flex items-center gap-3 text-xl text-[var(--color-primary)] m-0"><MdWork size={24} /> Job Roles</h2>
        <div className="flex gap-3 items-center flex-col md:flex-row">
          <select 
            value={selectedCountry} 
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="px-4 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm bg-white text-[var(--color-text-primary)] min-w-[200px]"
          >
            <option value="">Select Country</option>
            {countries.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
          <button
            type="button"
            className="flex items-center gap-2 px-5 py-2.5 bg-[var(--color-accent-green)] text-white border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out hover:bg-[var(--color-accent-green-dark)] disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => { setEditingRole(null); setShowAddModal(true); }}
            disabled={!selectedCountry}
          >
            <MdAdd size={20} /> Add Role
          </button>
        </div>
      </div>
      
      {selectedCountry ? (
        loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading job roles...</p>
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-4">
            {jobRoles
              .filter(role => role.countryId?._id === selectedCountry)
              .map(role => (
                <div key={role._id} className="bg-gray-50 border border-gray-100 rounded-md p-5 flex justify-between items-start transition-all duration-200 ease-in-out hover:border-[var(--color-accent-green)] hover:shadow-sm">
                  <div>
                    <h3 className="text-base text-[var(--color-primary)] m-0 mb-1">{role.title}</h3>
                    <p className="text-xs text-gray-400 m-0 mb-2">{role.countryId?.name}</p>
                    {role.description && (
                      <p className="text-[13px] text-gray-600 m-0 mb-2">{role.description}</p>
                    )}
                    <p className="text-[13px] text-gray-600 m-0">
                      {questionnaires.filter(q => q.jobRoleId === role._id).length} questionnaires
                    </p>
                    <p className="text-[11px] text-gray-500 m-0 mt-1">
                      Status: {role.isActive ? 'Active' : 'Inactive'}
                    </p>
                    <p className="text-[11px] text-gray-500 m-0 mt-1">
                      Agreement: {role.agreementFileUrl ? 'Configured' : 'Not set'}
                    </p>
                    {role.agreementAmount !== undefined && role.agreementAmount !== null && (
                      <p className="text-[11px] text-gray-500 m-0 mt-1">
                        Amount: {role.agreementAmount}
                      </p>
                    )}
                    {role.agreementFileUrl && (
                      <a
                        href={role.agreementFileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[12px] text-[#007bff] mt-1 inline-block"
                      >
                        View Agreement
                      </a>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-blue-600 text-white hover:bg-blue-800"
                      onClick={() => handleEditRole(role)}
                      title="Edit"
                    >
                      <MdEdit size={18} />
                    </button>
                    <button
                      className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-red-600 text-white hover:bg-red-800"
                      onClick={() => handleDeleteRole(role._id)}
                      title="Delete"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )
      ) : (
        <div className="flex flex-col items-center justify-center py-[60px] px-5 text-gray-400">
          <MdWork size={48} className="mb-4 opacity-50" />
          <p className="text-sm m-0">Select a country to view and manage roles</p>
        </div>
      )}

      {showAddModal && selectedCountry && (
        <AddRoleModal
          onClose={() => { setShowAddModal(false); setEditingRole(null); }}
          onAdd={handleAddRole}
          country={countries.find(c => c._id === selectedCountry)?.name}
          editingRole={editingRole}
        />
      )}
    </div>
  );

  const renderQuestionnairesTab = () => (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center pb-4 border-b-2 border-gray-100 flex-col md:flex-row gap-3">
        <h2 className="flex items-center gap-3 text-xl text-[var(--color-primary)] m-0"><MdQuiz size={24} /> Questionnaires</h2>
        <div className="flex gap-3 items-center flex-col md:flex-row">
          <select 
            value={selectedRole} 
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-4 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm bg-white text-[var(--color-text-primary)] min-w-[200px]"
          >
            <option value="">Select Role</option>
            {jobRoles.map(role => (
              <option key={role._id} value={role._id}>{role.title} ({role.countryId?.name})</option>
            ))}
          </select>
          <button 
            type="button"
            className="flex items-center gap-2 px-5 py-2.5 bg-[var(--color-accent-green)] text-white border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out hover:bg-[var(--color-accent-green-dark)] disabled:opacity-50 disabled:cursor-not-allowed" 
            onClick={() => setShowAddModal(true)}
            disabled={!selectedRole}
          >
            <MdAdd size={20} /> Add Question
          </button>
        </div>
      </div>
      
      {selectedRole ? (
        loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">Loading questionnaire...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {(() => {
              console.log('🔍 Looking for questionnaire with selectedRole:', selectedRole);
              console.log('📋 Available questionnaires:', questionnaires.map(q => ({
                id: q._id,
                jobRoleId: q.jobRoleId,
                jobRoleIdString: typeof q.jobRoleId === 'string' ? q.jobRoleId : q.jobRoleId?._id,
                title: q.title
              })));
              
              const questionnaire = questionnaires.find(q => {
                const roleId = typeof q.jobRoleId === 'string' ? q.jobRoleId : q.jobRoleId?._id;
                return roleId === selectedRole;
              });
              
              console.log('🎯 Found questionnaire:', questionnaire);
              const questions = questionnaire?.questions || [];
              console.log('❓ Questions:', questions);
              
              return questions.length > 0 ? questions.map((question, index) => (
                <div key={index} className="bg-gray-50 border border-gray-100 rounded-md p-5 border-l-4 border-l-[var(--color-accent-green)]">
                  {editingItem === `${questionnaire._id}-${index}` ? (
                    <EditQuestionForm
                      question={question}
                      onSave={(data) => handleEditQuestion(questionnaire._id, index, data)}
                      onCancel={() => setEditingItem(null)}
                    />
                  ) : (
                    <>
                      <div className="flex gap-3 items-center mb-3">
                        <span className="bg-[var(--color-accent-green)] text-white px-3 py-1 rounded-full text-xs font-semibold">Q{index + 1}</span>
                        <span className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-semibold">{question.fieldType || question.type}</span>
                        {question.required && <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">Required</span>}
                      </div>
                      <p className="text-[15px] text-[var(--color-primary)] m-0 mb-3 font-medium leading-relaxed">{question.questionText || question.question}</p>
                      {question.options && (
                        <div className="text-[13px] text-gray-600 m-0 mb-3">
                          <strong>Options:</strong> {question.options.join(', ')}
                        </div>
                      )}
                      <div className="flex gap-2 justify-end">
                        <button 
                          className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-[var(--color-accent-green)] text-white hover:bg-[var(--color-accent-green-dark)]"
                          onClick={() => setEditingItem(`${questionnaire._id}-${index}`)}
                          title="Edit"
                        >
                          <MdEdit size={18} />
                        </button>
                        <button 
                          className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-red-600 text-white hover:bg-red-800"
                          onClick={() => handleDeleteQuestion(questionnaire._id, index)}
                          title="Delete"
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )) : (
                <div className="text-center py-8">
                  <p className="text-gray-600">No questions added yet for this role.</p>
                </div>
              );
            })()}
          </div>
        )
      ) : (
        <div className="flex flex-col items-center justify-center py-[60px] px-5 text-gray-400">
          <MdQuiz size={48} className="mb-4 opacity-50" />
          <p className="text-sm m-0">Select a role to view and manage questionnaire</p>
        </div>
      )}

      {showAddModal && selectedRole && (
        <AddQuestionModal 
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddQuestion}
          role={jobRoles.find(r => r._id === selectedRole)?.title}
        />
      )}
    </div>
  );

  return (
    <MainLayout>
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="flex justify-between items-center mb-6 p-6 bg-white rounded-lg shadow-sm">
          <div>
            <h1 className="text-[28px] text-[var(--color-primary)] m-0 mb-1">Job Management</h1>
            <p className="text-sm text-gray-600 m-0">Manage countries, roles, and questionnaires</p>
          </div>
          <div className="flex gap-3">
            <button 
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white border-0 rounded-md font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-blue-700 hover:-translate-y-px" 
              onClick={() => navigate('/admin/create-application')}
            >
              <MdAdd size={20} /> Create Application
            </button>
            <button className="flex items-center gap-2 px-6 py-3 bg-[var(--color-accent-green)] text-white border-0 rounded-md font-semibold cursor-pointer transition-all duration-200 ease-in-out hover:bg-[var(--color-accent-green-dark)] hover:-translate-y-px" onClick={handleSaveChanges}>
              <MdSave size={20} /> Save All Changes
            </button>
          </div>
        </div>

        {/* Debug Section */}
        <div className="mb-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Debug Information</h3>
          <div className="text-sm">
            <p><strong>Loading:</strong> {loading ? 'Yes' : 'No'}</p>
            <p><strong>Countries loaded:</strong> {countries.length}</p>
            <p><strong>Job roles loaded:</strong> {jobRoles.length}</p>
            <p><strong>Questionnaires loaded:</strong> {questionnaires.length}</p>
            <p><strong>Selected Country:</strong> {selectedCountry || 'None'}</p>
            <p><strong>Selected Role:</strong> {selectedRole || 'None'}</p>
            <p><strong>Error:</strong> {error || 'None'}</p>
            {jobRoles.length > 0 && (
              <div className="mt-2">
                <strong>Job Roles:</strong>
                <ul className="ml-4">
                  {jobRoles.map(role => (
                    <li key={role._id}>{role.title} ({role.countryId?.name})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 mb-6 bg-white p-2 rounded-lg shadow-sm">
          <button 
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-transparent border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out ${activeTab === 'countries' ? 'bg-[var(--color-accent-green)] text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-[var(--color-primary)]'}`}
            onClick={() => setActiveTab('countries')}
          >
            <MdPublic size={20} /> Countries
          </button>
          <button 
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-transparent border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out ${activeTab === 'roles' ? 'bg-[var(--color-accent-green)] text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-[var(--color-primary)]'}`}
            onClick={() => setActiveTab('roles')}
          >
            <MdWork size={20} /> Roles
          </button>
          <button 
            className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-transparent border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out ${activeTab === 'questionnaires' ? 'bg-[var(--color-accent-green)] text-white' : 'text-gray-600 hover:bg-gray-50 hover:text-[var(--color-primary)]'}`}
            onClick={() => setActiveTab('questionnaires')}
          >
            <MdQuiz size={20} /> Questionnaires
          </button>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm min-h-[500px]">
          {activeTab === 'countries' && renderCountriesTab()}
          {activeTab === 'roles' && renderRolesTab()}
          {activeTab === 'questionnaires' && renderQuestionnairesTab()}
        </div>
      </div>
    </MainLayout>
  );
};

// Modal Components
const AddCountryModal = ({ onClose, onAdd, editingItem, formData, setFormData }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-[500px] w-[90%] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl text-[var(--color-primary)] m-0 mb-5">{editingItem ? 'Edit Country' : 'Add New Country'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Country Name</label>
            <input
              type="text"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.label}
              onChange={(e) => setFormData({...formData, label: e.target.value})}
              placeholder="e.g., United Arab Emirates"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Country Code (Optional)</label>
            <input
              type="text"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.value}
              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              placeholder="e.g., UAE"
              maxLength={10}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Agreement Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.agreementAmount}
              onChange={(e) => setFormData({ ...formData, agreementAmount: e.target.value })}
              placeholder="e.g., 5000"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Country Agreement (PDF/Image)</label>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              onChange={(e) => setFormData({ ...formData, agreementFile: e.target.files?.[0] || null })}
            />
            <p className="text-xs text-gray-500 mt-1 mb-0">Optional, but required for candidates to proceed in Agreement stage.</p>
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button type="button" className="px-5 py-2.5 border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out bg-gray-200 text-[var(--color-text-primary)] border-[1.5px] border-gray-300 hover:bg-gray-50" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-5 py-2.5 border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out bg-[var(--color-accent-green)] text-white hover:bg-[var(--color-accent-green-dark)]">Add Country</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddRoleModal = ({ onClose, onAdd, country, editingRole }) => {
  const [formData, setFormData] = useState({
    label: editingRole?.title || '',
    description: editingRole?.description || '',
    agreementAmount: editingRole?.agreementAmount ?? '',
    agreementFile: null
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(formData);
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-[500px] w-[90%] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl text-[var(--color-primary)] m-0 mb-5">
          {editingRole ? `Edit Role — ${country}` : `Add New Role to ${country}`}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Role Name</label>
            <input
              type="text"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.label}
              onChange={(e) => setFormData({...formData, label: e.target.value})}
              placeholder="e.g., Senior Nurse"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Description (Optional)</label>
            <textarea
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] resize-y focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description of the role..."
              rows={3}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Agreement Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.agreementAmount}
              onChange={(e) => setFormData({ ...formData, agreementAmount: e.target.value })}
              placeholder="e.g., 5000"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">
              Role Agreement (PDF/Image){editingRole?.agreementFileUrl ? ' — replaces existing' : ''}
            </label>
            {editingRole?.agreementFileUrl && (
              <a
                href={editingRole.agreementFileUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#007bff] mb-2 inline-block"
              >
                View current agreement
              </a>
            )}
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              onChange={(e) => setFormData({ ...formData, agreementFile: e.target.files?.[0] || null })}
            />
            <p className="text-xs text-gray-500 mt-1 mb-0">
              {editingRole ? 'Optional — only upload to replace the current agreement.' : 'Optional, but required for candidates to proceed in Agreement stage.'}
            </p>
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button type="button" className="px-5 py-2.5 border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out bg-gray-200 text-[var(--color-text-primary)] border-[1.5px] border-gray-300 hover:bg-gray-50" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-5 py-2.5 border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out bg-[var(--color-accent-green)] text-white hover:bg-[var(--color-accent-green-dark)]">
              {editingRole ? 'Save Changes' : 'Add Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddQuestionModal = ({ onClose, onAdd, role }) => {
  const [formData, setFormData] = useState({
    question: '',
    type: 'radio',
    options: '',
    placeholder: '',
    required: 'true'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const questionData = {
      ...formData,
      options: formData.options ? formData.options.split(',').map(o => o.trim()) : undefined
    };
    onAdd(questionData);
  };

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-[700px] w-[90%] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl text-[var(--color-primary)] m-0 mb-5">Add Question to {role}</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Question</label>
            <textarea
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] resize-y focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.question}
              onChange={(e) => setFormData({...formData, question: e.target.value})}
              placeholder="Enter your question..."
              required
              rows={3}
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Question Type</label>
            <select
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.type}
              onChange={(e) => setFormData({...formData, type: e.target.value})}
            >
              <option value="radio">Radio (Single Choice)</option>
              <option value="checkbox">Checkbox (Multiple Choice)</option>
              <option value="select">Dropdown</option>
              <option value="textarea">Text Area</option>
            </select>
          </div>
          {['radio', 'checkbox', 'select'].includes(formData.type) && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Options (comma-separated)</label>
              <input
                type="text"
                className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
                value={formData.options}
                onChange={(e) => setFormData({...formData, options: e.target.value})}
                placeholder="Yes, No, Maybe"
                required
              />
            </div>
          )}
          {formData.type === 'textarea' && (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Placeholder</label>
              <input
                type="text"
                className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
                value={formData.placeholder}
                onChange={(e) => setFormData({...formData, placeholder: e.target.value})}
                placeholder="Enter placeholder text..."
              />
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Required</label>
            <select
              className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
              value={formData.required}
              onChange={(e) => setFormData({...formData, required: e.target.value})}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="flex gap-3 justify-end mt-5">
            <button type="button" className="px-5 py-2.5 border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out bg-gray-200 text-[var(--color-text-primary)] border-[1.5px] border-gray-300 hover:bg-gray-50" onClick={onClose}>Cancel</button>
            <button type="submit" className="px-5 py-2.5 border-0 rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 ease-in-out bg-[var(--color-accent-green)] text-white hover:bg-[var(--color-accent-green-dark)]">Add Question</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const EditQuestionForm = ({ question, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    question: question.questionText || question.question,
    type: question.fieldType || question.type,
    options: question.options ? question.options.join(', ') : '',
    placeholder: question.placeholder || '',
    required: question.required
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedData = {
      ...formData,
      options: formData.options ? formData.options.split(',').map(o => o.trim()) : undefined
    };
    onSave(updatedData);
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-white rounded-md">
      <div className="mb-4">
        <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Question</label>
        <textarea
          className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] resize-y focus:outline-none focus:border-[var(--color-accent-green)]"
          value={formData.question}
          onChange={(e) => setFormData({...formData, question: e.target.value})}
          required
          rows={2}
        />
      </div>
      {['radio', 'checkbox', 'select'].includes(formData.type) && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">Options (comma-separated)</label>
          <input
            type="text"
            className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-300 rounded-md text-sm font-[inherit] focus:outline-none focus:border-[var(--color-accent-green)]"
            value={formData.options}
            onChange={(e) => setFormData({...formData, options: e.target.value})}
            required
          />
        </div>
      )}
      <div className="flex gap-2 justify-end mt-3">
        <button type="submit" className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-[var(--color-accent-green)] text-white">
          <MdSave size={18} /> Save
        </button>
        <button type="button" className="p-2 border-0 rounded cursor-pointer transition-all duration-200 ease-in-out flex items-center gap-1 text-[13px] font-semibold bg-gray-200 text-gray-600" onClick={onCancel}>
          <MdCancel size={18} /> Cancel
        </button>
      </div>
    </form>
  );
};

export default AdminJobManagementPage;
